import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import { createEmptyDraft } from '../constants';
import type {
  DashboardCalculation,
  DashboardColumnKind,
  DashboardDefinition,
  DashboardDraft,
  DashboardField,
  DashboardFilter,
  DashboardLink,
  DashboardSourceTable,
  DashboardWidgetFormat,
  DashboardWidgetType,
} from '../types';
import type { DashboardAssistantAction } from '../services/dashboardAssistantContract';
import * as groupOps from './groupOperations';
import * as pageOps from './pageOperations';
import { applyProposal } from './proposalOperations';
import type { ApplyProposalResult } from './proposalOperations';
import * as widgetOps from './widgetOperations';
import type { GroupMoveDirection } from './groupOperations';
import type { WidgetRect, WidgetSize } from './widgetOperations';
import type { DashboardWidgetSection } from '../widgetValidation';

/**
 * Holds the in-memory dashboard being edited, from the creation screen through
 * the canvas.
 *
 * Why a context and not `useState` in the page: the form and the editor are two
 * **routes** over one object (`dashboards/new` and `dashboards/new/editor`). The
 * name is typed on the form and has to survive the navigation, and from SL-1659
 * on the sources editor is reopened *from* the canvas as a modal and writes back
 * into the same draft. The provider hangs off the section's parent route, which
 * does not remount when moving between its children — that is what makes one
 * source of truth possible without passing the draft through navigation state.
 *
 * Why not react-query: this is not a cache of anything the server holds. Server
 * state in this feature is the table catalog, and that one does go through
 * react-query (see `hooks/useAccountTables.ts`).
 *
 * The draft does NOT survive a reload — the auto-saved draft the PRD describes
 * is SL-1671. A refresh on `dashboards/new` starts over, which is the honest
 * behaviour until there is a place to store it.
 */
interface DashboardDraftContextValue {
  draft: DashboardDraft;
  /** Replace the dashboard name (the creation screen and, later, the canvas header). */
  setName: (name: string) => void;
  /**
   * Set the main table — the dashboard's grain.
   *
   * Changing it drops the declared relations: a relation travels *through* a
   * column of the main table, so once that table is another one the relations no
   * longer point anywhere. Dropping them here keeps the draft from ever holding
   * a relation whose `viaColumnId` does not exist.
   */
  setMainTable: (table: DashboardSourceTable | null) => void;
  /** Replace the declared relations. */
  setLinks: (links: DashboardLink[]) => void;
  /**
   * Drops relation rows nobody filled in.
   *
   * Called on submit and not on every change: clearing them as you type would
   * remove the row the user just opened, before they got to it. A row with one
   * half filled in is NOT dropped — that one is broken, and it blocks creating.
   */
  dropBlankLinks: () => void;
  /** Start over with an empty dashboard in the given folder. */
  resetDraft: (folderId?: string | null) => void;

  /**
   * The id of the dashboard being edited, or `null` while it is only a draft.
   *
   * This is what makes Save a create the first time and a replace afterwards —
   * the header has no second button, and whether the dashboard exists yet is
   * not something a button should have to remember.
   */
  dashboardId: string | null;
  /**
   * Load a stored dashboard into the draft, and treat it as the saved state.
   *
   * The selection is cleared with it: a page or widget picked in whatever was
   * open before does not exist in this one.
   */
  hydrateDraft: (definition: DashboardDefinition) => void;
  /**
   * Record that this exact draft is what the server now holds.
   *
   * Takes the draft that was **sent**, not the one on screen. They differ
   * whenever the user keeps editing while the request is in flight, and marking
   * the on-screen one as saved would silently swallow those edits — the draft
   * is replaced, never mutated, so holding the reference is enough.
   */
  markSaved: (dashboardId: string, savedDraft: DashboardDraft) => void;
  /**
   * Whether the draft differs from what was last saved.
   *
   * On a dashboard that has never been saved, "last saved" is the empty draft
   * it started as — so a name typed in the creation form already counts as
   * something to lose.
   */
  isDirty: boolean;

  /**
   * The page the canvas is showing. Always one of `draft.pages`: if the one that
   * was showing is gone, this resolves to the first, so "there is always a
   * visible page" holds by construction — the same shape as `activeGroupId`.
   */
  activePageId: string;
  setActivePageId: (pageId: string) => void;
  /**
   * Appends a page at the end of the bar, born with one empty group, and shows
   * it. That group becomes the destination, which is what makes the edge case
   * "the active group belongs to the visible page" true on arrival.
   *
   * The name arrives resolved: it is display text, and the store holds no i18n.
   */
  addPage: (name: string) => void;
  /** Renames a page. No-op on an empty name; uniqueness is the caller's to check. */
  renamePage: (pageId: string, name: string) => void;
  /**
   * Deletes a page, its groups and their widgets. No-op on the last page.
   *
   * Confirming is the caller's business, same as with a group: the confirmation
   * has to name how many widgets go with it, and copy does not belong here.
   */
  deletePage: (pageId: string) => void;
  /**
   * The group everything added lands in — by the controls or by the assistant
   * (PRD §7). Always a group of the active page: if the one that was active is
   * gone, this resolves to the first one, so "there is always an active group"
   * holds by construction and not by every caller remembering to fix it up.
   */
  activeGroupId: string;
  setActiveGroupId: (groupId: string) => void;
  /**
   * Appends an empty group at the bottom of the active page; it becomes active.
   *
   * Returns its id — a setter that returns something, on purpose: the caller has
   * to bring the new group into view, and the alternative was a second piece of
   * shared state whose only job was to say "this one is new".
   */
  /**
   * Adds a group. `fallbackPageName` names the page created alongside it when
   * the dashboard has none — the caller supplies it because naming is localised
   * and this context is not.
   *
   * Required rather than defaulted: an empty default is a page with no name,
   * which the bar then labels by position. That is a reasonable page and a
   * terrible accident, and nothing would have told the caller it happened.
   */
  addGroup: (fallbackPageName: string) => string;
  renameGroup: (groupId: string, title: string) => void;
  moveGroup: (groupId: string, direction: GroupMoveDirection) => void;
  /** Drops the dragged group where another one sits, pushing the rest along. */
  reorderGroups: (activeId: string, overId: string) => void;
  /**
   * Deletes a group and the widgets it held. No-op on the last group of a page.
   *
   * Confirming is the caller's business: the confirmation has to name how many
   * widgets go with it, which is copy, and copy does not belong in the store.
   */
  deleteGroup: (groupId: string) => void;

  /**
   * The widget the panel is configuring, or `''` when none is selected.
   *
   * Resolved the same way the active page and group are: what the user picked
   * is only an intent, and a widget can be deleted or its group can go with it.
   * Nothing outside sees the raw pick.
   *
   * `''` and not `null` because "nothing selected" is a normal, frequent state
   * here — clicking empty canvas deselects (PRD Feature 3) — and the two active
   * ids above already use the empty string for "none".
   */
  activeWidgetId: string;
  setActiveWidgetId: (widgetId: string) => void;

  /**
   * Appends an empty widget to a group; it becomes the selected one.
   *
   * `groupId` defaults to the active group, which is the PRD's rule for the
   * header's Add. A caller that names a group — a group's own Add — passes it,
   * because making it active first would not be visible to this call until the
   * next render.
   *
   * Returns its id for the same reason `addGroup` does: the caller has to bring
   * it into view and open the panel on it.
   */
  addWidget: (groupId: string | undefined, fallbackPageName: string) => string;
  /** Copies a widget next to the original and selects the copy. */
  duplicateWidget: (widgetId: string) => string;
  /**
   * Puts a widget at a rectangle of a group, its own or another's.
   *
   * `rect` is what a pointer drop measured. Without one the widget keeps its
   * size and lands in the first free spot of its destination.
   */
  moveWidget: (widgetId: string, toGroupId: string, rect?: Partial<WidgetRect>) => void;
  /** Resizes a widget. Out-of-range sizes are clamped, never rejected. */
  resizeWidget: (widgetId: string, size: WidgetSize) => void;
  /** Renames a widget. A blank title clears it, it does not store `''`. */
  renameWidget: (widgetId: string, title: string) => void;
  setWidgetType: (widgetId: string, type: DashboardWidgetType) => void;
  /**
   * Replaces the contents of one well — add, remove and reorder are one edit.
   *
   * `isNumeric` decides which calculation a bare column starts on. It is passed
   * in rather than looked up because the schema lives behind react-query and
   * the store is not where a fetch belongs.
   */
  setWell: (
    widgetId: string,
    wellKey: string,
    fields: DashboardField[],
    isNumeric?: (columnId: string) => boolean,
  ) => void;
  setFieldCalculation: (
    widgetId: string,
    wellKey: string,
    columnId: string,
    calculation: DashboardCalculation,
  ) => void;
  /**
   * Adds a filter over a column, opening on the operator its kind starts with.
   *
   * `kind` is passed in for the same reason `setWell` takes `isNumeric`: it
   * comes from the schema, and the store is not where a fetch belongs.
   */
  addFilter: (widgetId: string, columnId: string, kind: DashboardColumnKind) => void;
  /** Replaces one filter, whole — the union keeps operator and value in step. */
  setFilter: (widgetId: string, filterId: string, filter: DashboardFilter) => void;
  /**
   * Moves a filter to another column, keeping the operator when the new kind
   * still offers it and always clearing the value.
   */
  setFilterColumn: (
    widgetId: string,
    filterId: string,
    columnId: string,
    kind: DashboardColumnKind,
  ) => void;
  removeFilter: (widgetId: string, filterId: string) => void;
  /** Changes presentation. A property set to `undefined` goes back to its default. */
  setWidgetFormat: (widgetId: string, patch: Partial<DashboardWidgetFormat>) => void;
  /** Sets the line read next to the title. Blank clears it, it does not store `''`. */
  setWidgetDescription: (widgetId: string, description: string) => void;
  /**
   * Deletes a widget. Direct, with no guard and no last-one rule.
   *
   * "Borrar: directo, sin confirmación — un widget es barato de rehacer"
   * (PRD Feature 3), which is why this one has no counterpart to the caller-side
   * confirmation `deleteGroup` and `deletePage` need.
   */
  deleteWidget: (widgetId: string) => void;

  /**
   * Selects a widget **and asks the panel to flash**, for the unconfigured
   * card's "show me where".
   *
   * The card and the panel sit in different branches of the tree, so the ask
   * travels through the store rather than through a prop drilled across the
   * canvas.
   */
  showWidgetSettings: (widgetId: string, section?: DashboardWidgetSection) => void;
  /**
   * A counter, not a boolean.
   *
   * Asking twice in a row has to flash twice, and a boolean that is already
   * `true` cannot say "again" — the animation would never restart. The panel
   * keys its flash on this number, which is what remounts it.
   */
  panelPulse: number;
  /**
   * The section the last ask wants the panel standing in, or `null` for "wherever
   * it was".
   *
   * The card names it because the card is what knows **which field** the user
   * just asked about: SL-1663's first acceptance criterion is that pressing an
   * unconfigured widget opens the panel *on the section that owns the missing
   * field*, not on the section the panel happened to be left on. Read together
   * with `panelPulse`, which is what makes asking twice count twice.
   */
  panelSection: DashboardWidgetSection | null;
  /** Called by the panel when its flash finishes. */
  clearPanelPulse: () => void;

  /**
   * Applies an assistant's proposal (SL-1667): every action or none, in one
   * write, so it counts as one unsaved change — applying is not saving.
   *
   * What it creates lands on the active page and group unless the proposal
   * names others, becomes the selection, and is marked (`landed`) so the canvas
   * can show where it went. A refusal changes nothing and says why.
   */
  applyAssistantProposal: (actions: DashboardAssistantAction[]) => ApplyProposalResult;
  /**
   * What the last applied proposal created, so each card can mark itself for a
   * moment. `key` is a counter for the same reason `panelPulse` is: applying
   * twice has to mark twice. Never persisted.
   */
  landed: LandedMark;
  /** Called by a card when its mark finishes. */
  clearLanded: () => void;
  /**
   * Which dashboard the editor is on, as a counter: it moves every time the
   * draft is replaced whole (`resetDraft`, `hydrateDraft`) and never on an edit.
   *
   * What lives only as long as one dashboard keys itself on this — the
   * assistant's conversation is the case: moving from one saved dashboard to
   * another keeps the same page mounted, and a proposal made for the first one
   * must not be applicable to the second.
   */
  draftSession: number;
}

const DashboardDraftContext = createContext<DashboardDraftContextValue | null>(null);

/** What the last applied proposal created, for the cards to mark themselves. */
export interface LandedMark {
  ids: readonly string[];
  key: number;
}

const NOTHING_LANDED: LandedMark = Object.freeze({ ids: [], key: 0 });

interface DashboardDraftProviderProps {
  children: React.ReactNode;
  /** Folder the dashboard is being created in. `null` = root of the Home. */
  folderId?: string | null;
}

export const DashboardDraftProvider: React.FC<DashboardDraftProviderProps> = ({
  children,
  folderId = null,
}) => {
  // `folderId` seeds the first draft only. It is deliberately NOT a dependency
  // that rebuilds the draft: the Home passes the folder once, and rebuilding
  // mid-flow would wipe what the user already typed.
  const [draft, setDraft] = useState<DashboardDraft>(() => createEmptyDraft(folderId));

  // `null` until the dashboard exists on the server — see `dashboardId` above.
  const [dashboardId, setDashboardId] = useState<string | null>(null);
  // The draft as it was last written down, to compare against. Seeded with the
  // empty draft rather than left null, so "nothing to lose" is a real
  // comparison from the first render instead of a special case.
  //
  // Seeded with **the same object** `draft` started as, not a second call to
  // `createEmptyDraft`: that function mints a fresh uuid for its page and its
  // group every time, so two empty drafts are never equal and the editor would
  // open already claiming unsaved changes.
  const [savedDraft, setSavedDraft] = useState<DashboardDraft>(draft);

  // What the user last picked, in both axes. Neither is the answer on its own:
  // a page or a group can be deleted, and a group can belong to another page.
  // The resolved `activePageId` / `activeGroupId` below are the only ones
  // anybody outside gets to see.
  const [pickedPageId, setPickedPageId] = useState<string | null>(null);
  const [pickedGroupId, setPickedGroupId] = useState<string | null>(null);
  const [pickedWidgetId, setPickedWidgetId] = useState<string | null>(null);
  const [panelPulse, setPanelPulse] = useState(0);
  const [panelSection, setPanelSection] = useState<DashboardWidgetSection | null>(null);
  const [landed, setLanded] = useState<LandedMark>(NOTHING_LANDED);
  const [draftSession, setDraftSession] = useState(0);

  const activePageId =
    pickedPageId && draft.pages.some((page) => page.id === pickedPageId)
      ? pickedPageId
      : (draft.pages[0]?.id ?? '');

  const pageGroupIds = groupOps.groupIdsOfPage(draft, activePageId);
  const activeGroupId =
    pickedGroupId && pageGroupIds.includes(pickedGroupId) ? pickedGroupId : (pageGroupIds[0] ?? '');

  // Unlike the page and the group, there is no fallback: nothing selected is a
  // legitimate state of the canvas — that is what clicking empty space does —
  // so a deleted widget leaves the panel with nothing, not with its neighbour.
  //
  // Resolved against the **visible page** and not merely against the widgets
  // that exist: a widget left selected on another page would keep the panel
  // pointed at something nobody can see. Going back to its page finds it again.
  const pageWidgetIds = new Set(
    draft.groups
      .filter((group) => pageGroupIds.includes(group.id))
      .flatMap((group) => group.widgetIds),
  );
  const activeWidgetId = pickedWidgetId && pageWidgetIds.has(pickedWidgetId) ? pickedWidgetId : '';

  const setName = useCallback((name: string) => {
    setDraft((current) => ({ ...current, name }));
  }, []);

  const setMainTable = useCallback((mainTable: DashboardSourceTable | null) => {
    setDraft((current) => ({ ...current, sources: { mainTable, links: [] } }));
  }, []);

  const setLinks = useCallback((links: DashboardLink[]) => {
    setDraft((current) => ({ ...current, sources: { ...current.sources, links } }));
  }, []);

  const dropBlankLinks = useCallback(() => {
    setDraft((current) => ({
      ...current,
      sources: {
        ...current.sources,
        links: current.sources.links.filter((link) => link.tableId || link.viaColumnId),
      },
    }));
  }, []);

  const resetDraft = useCallback((nextFolderId: string | null = null) => {
    const empty = createEmptyDraft(nextFolderId);
    setDraft(empty);
    setDashboardId(null);
    setSavedDraft(empty);
    setPickedPageId(null);
    setPickedGroupId(null);
    setPickedWidgetId(null);
    setPanelPulse(0);
    setLanded(NOTHING_LANDED);
    setDraftSession((session) => session + 1);
  }, []);

  const hydrateDraft = useCallback((definition: DashboardDefinition) => {
    const { id, ...stored } = definition;
    setDraft(stored);
    setDashboardId(id);
    setSavedDraft(stored);
    setPickedPageId(null);
    setPickedGroupId(null);
    setPickedWidgetId(null);
    // What the previous dashboard asked the panel to point at is not a question
    // about this one — and a pulse left over would replay on it.
    setPanelPulse(0);
    setPanelSection(null);
    setLanded(NOTHING_LANDED);
    setDraftSession((session) => session + 1);
  }, []);

  const markSaved = useCallback((savedId: string, sent: DashboardDraft) => {
    setDashboardId(savedId);
    setSavedDraft(sent);
  }, []);

  // `isEqual` and not a hand-rolled serializer: lodash is already a dependency
  // and already the repo's deep comparison (`app/src/utils/helpers.ts`). Its one
  // difference from a key-sorted stringify — that `{a:1}` and `{a:1,b:undefined}`
  // read as different — cannot bite here, because `widgetOperations` never
  // leaves a key holding `undefined`: it deletes the key instead, deliberately
  // and with the reason written down beside each one.
  //
  // Memoised on the draft rather than compared per render: the walk covers the
  // whole tree, the provider re-renders on every selection change, and the draft
  // is replaced rather than mutated — so this is exact and runs once per edit.
  const isDirty = useMemo(() => !isEqual(draft, savedDraft), [draft, savedDraft]);

  // The group operations read the draft off the closure instead of the updater's
  // argument, the way `useDashboardLinks` does: `addGroup` has to hand its new id
  // to `setPickedGroupId`, and an updater that is allowed to run twice would mint
  // two ids and pick the wrong one.
  /**
   * Adds a group, and the page to hold it when there is none.
   *
   * A dashboard can legally have no pages — one saved before anything was built
   * on it comes back as `pages: []` — and `groupOps.addGroup` writes the group
   * into `draft.groups` and then looks for the page to attach it to. With no
   * page it attaches to nothing: the group exists, no page references it, and
   * the canvas stays empty while the draft turns dirty. The button read as
   * doing nothing while it quietly filled the document with orphans.
   *
   * Seeding here rather than in the caller, and in ONE update: a caller that
   * added the page and then called this would still read `activePageId` off the
   * closure it entered with, find it empty, and orphan the group anyway.
   *
   * `pageOps.addPage` already creates its page with one group inside, so that
   * group IS the one being asked for — a second would leave an empty extra.
   */
  const addGroup = useCallback(
    (fallbackPageName: string) => {
      const hasPage = draft.pages.some((page) => page.id === activePageId);

      if (!hasPage) {
        const seeded = pageOps.addPage(draft, fallbackPageName);
        setDraft(seeded.draft);
        setPickedPageId(seeded.pageId);
        setPickedGroupId(seeded.groupId);
        return seeded.groupId;
      }

      const { draft: next, groupId } = groupOps.addGroup(draft, activePageId);
      setDraft(next);
      setPickedGroupId(groupId);
      return groupId;
    },
    [draft, activePageId],
  );

  const renameGroup = useCallback(
    (groupId: string, title: string) => {
      setDraft(groupOps.renameGroup(draft, groupId, title));
    },
    [draft],
  );

  const moveGroup = useCallback(
    (groupId: string, direction: GroupMoveDirection) => {
      setDraft(groupOps.moveGroup(draft, activePageId, groupId, direction));
    },
    [draft, activePageId],
  );

  // Same shape as the group operations: read the draft off the closure, and let
  // the resolution above deal with what the deletion left behind.
  const addPage = useCallback(
    (name: string) => {
      const { draft: next, pageId, groupId } = pageOps.addPage(draft, name);
      setDraft(next);
      setPickedPageId(pageId);
      setPickedGroupId(groupId);
    },
    [draft],
  );

  const renamePage = useCallback(
    (pageId: string, name: string) => {
      setDraft(pageOps.renamePage(draft, pageId, name));
    },
    [draft],
  );

  const deletePage = useCallback(
    (pageId: string) => {
      setDraft(pageOps.deletePage(draft, pageId));
    },
    [draft],
  );

  const reorderGroups = useCallback(
    (activeId: string, overId: string) => {
      setDraft(groupOps.reorderGroups(draft, activePageId, activeId, overId));
    },
    [draft, activePageId],
  );

  // No cleanup of `pickedGroupId` afterwards: with the group gone, the resolution
  // above already falls back to the first one of the page.
  const deleteGroup = useCallback(
    (groupId: string) => {
      setDraft(groupOps.deleteGroup(draft, activePageId, groupId));
    },
    [draft, activePageId],
  );

  // Same shape again: read the draft off the closure so `addWidget` can hand its
  // new id to `setPickedWidgetId` without an updater minting two.
  //
  // ⚠️ `groupId` is a **parameter** and not a read of `activeGroupId`, and that is
  // load-bearing. A caller that wants to add to another group has to make it
  // active first, and `setActiveGroupId` is a plain state setter: the change
  // lands on the next render, while this call runs in the same tick with the
  // previous value still in its closure. The widget landed in the group that
  // *was* active. Taking the destination as an argument removes the ordering
  // problem instead of asking every caller to work around it.
  const addWidget = useCallback(
    (groupId: string = activeGroupId, fallbackPageName: string) => {
      /*
       * Same hole as `addGroup`, one level down, and in TWO shapes rather than
       * one. A widget needs a group; a group needs a page. What is missing
       * decides how much has to be built, and asking only "does this group
       * exist" cannot tell the two apart — `activeGroupId` is `''` both when
       * there is no page and when the page on screen holds no groups, so the
       * first version treated a page with no groups as no page at all and
       * created a SECOND one, navigating away from the page being looked at.
       */
      if (!draft.groups.some((group) => group.id === groupId)) {
        const page = draft.pages.find((candidate) => candidate.id === activePageId);

        // The page is there and empty: it gets the group, and keeps the screen.
        if (page) {
          const seeded = groupOps.addGroup(draft, page.id);
          const { draft: next, widgetId } = widgetOps.addWidget(seeded.draft, seeded.groupId);
          setDraft(next);
          setPickedGroupId(seeded.groupId);
          setPickedWidgetId(widgetId);
          return widgetId;
        }

        // Nothing at all: the page brings its own group, and the widget goes in.
        const seeded = pageOps.addPage(draft, fallbackPageName);
        const { draft: next, widgetId } = widgetOps.addWidget(seeded.draft, seeded.groupId);
        setDraft(next);
        setPickedPageId(seeded.pageId);
        setPickedGroupId(seeded.groupId);
        setPickedWidgetId(widgetId);
        return widgetId;
      }

      const { draft: next, widgetId } = widgetOps.addWidget(draft, groupId);
      setDraft(next);
      // The destination moves to where the widget landed. The argument is what
      // makes this safe: reading `activeGroupId` after a `setActiveGroupId` in
      // the same tick would still see the old one.
      setPickedGroupId(groupId);
      // ⚠️ **And the page moves with it.** `activeWidgetId` resolves against the
      // *visible* page, so a widget added to a group of another page was created,
      // drawn on its own page, and invisible to the panel — which said "nothing
      // selected" while the canvas showed the new card. The selection had not
      // failed; it was pointing somewhere nobody could see.
      //
      // Read off `next` and not `draft`: a group created in this same call would
      // not be in the old one yet.
      const pageId = pageOps.pageIdOfGroup(next, groupId);
      if (pageId) setPickedPageId(pageId);
      setPickedWidgetId(widgetId);
      return widgetId;
    },
    [draft, activeGroupId, activePageId],
  );

  const renameWidget = useCallback(
    (widgetId: string, title: string) => {
      setDraft(widgetOps.renameWidget(draft, widgetId, title));
    },
    [draft],
  );

  // An updater and not a read of `draft`: the settings panel calls this from the
  // confirmation's onOk, long after the render that opened it. A closure over that
  // render's draft would write it back and undo whatever happened in between.
  const setWidgetType = useCallback((widgetId: string, type: DashboardWidgetType) => {
    setDraft((current) => widgetOps.setWidgetType(current, widgetId, type));
  }, []);

  const setWell = useCallback(
    (
      widgetId: string,
      wellKey: string,
      fields: DashboardField[],
      isNumeric?: (columnId: string) => boolean,
    ) => {
      setDraft(widgetOps.setWell(draft, widgetId, wellKey, fields, isNumeric));
    },
    [draft],
  );

  const setFieldCalculation = useCallback(
    (widgetId: string, wellKey: string, columnId: string, calculation: DashboardCalculation) => {
      setDraft(widgetOps.setFieldCalculation(draft, widgetId, wellKey, columnId, calculation));
    },
    [draft],
  );

  const addFilter = useCallback(
    (widgetId: string, columnId: string, kind: DashboardColumnKind) => {
      setDraft(widgetOps.addFilter(draft, widgetId, columnId, kind));
    },
    [draft],
  );

  const setFilter = useCallback(
    (widgetId: string, filterId: string, filter: DashboardFilter) => {
      setDraft(widgetOps.setFilter(draft, widgetId, filterId, filter));
    },
    [draft],
  );

  const setFilterColumn = useCallback(
    (widgetId: string, filterId: string, columnId: string, kind: DashboardColumnKind) => {
      setDraft(widgetOps.setFilterColumn(draft, widgetId, filterId, columnId, kind));
    },
    [draft],
  );

  const removeFilter = useCallback(
    (widgetId: string, filterId: string) => {
      setDraft(widgetOps.removeFilter(draft, widgetId, filterId));
    },
    [draft],
  );

  const setWidgetFormat = useCallback(
    (widgetId: string, patch: Partial<DashboardWidgetFormat>) => {
      setDraft(widgetOps.setWidgetFormat(draft, widgetId, patch));
    },
    [draft],
  );

  const setWidgetDescription = useCallback(
    (widgetId: string, description: string) => {
      setDraft(widgetOps.setWidgetDescription(draft, widgetId, description));
    },
    [draft],
  );

  // No cleanup of `pickedWidgetId` either: with the widget gone, the resolution
  // above already falls back to nothing selected.
  const deleteWidget = useCallback(
    (widgetId: string) => {
      setDraft(widgetOps.deleteWidget(draft, widgetId));
    },
    [draft],
  );

  // The canvas half (SL-1662). Same shape as the rest: read the draft off the
  // closure, because the one that creates something has to hand its new id to
  // `setPickedWidgetId` and an updater React may run twice would mint two.
  const duplicateWidget = useCallback(
    (widgetId: string) => {
      const { draft: next, widgetId: copyId } = widgetOps.duplicateWidget(draft, widgetId);
      setDraft(next);
      if (copyId) setPickedWidgetId(copyId);
      return copyId;
    },
    [draft],
  );

  // The destination follows the drop. Putting a widget into a group is saying "I
  // am working here", and what gets added next should land where the user just
  // was — not where they last clicked before the drag.
  const moveWidget = useCallback(
    (widgetId: string, toGroupId: string, rect?: Partial<WidgetRect>) => {
      setDraft(widgetOps.moveWidget(draft, widgetId, toGroupId, rect));
      setPickedGroupId(toGroupId);
    },
    [draft],
  );

  const resizeWidget = useCallback(
    (widgetId: string, size: widgetOps.WidgetSize) => {
      setDraft(widgetOps.resizeWidget(draft, widgetId, size));
    },
    [draft],
  );

  const setActiveWidgetId = useCallback(
    (widgetId: string) => {
      setPickedWidgetId(widgetId);
      // Working on a widget is working on its group, so the destination of
      // whatever gets added next moves beside it rather than staying wherever
      // the user last clicked.
      const group = widgetId ? widgetOps.groupOfWidget(draft, widgetId) : undefined;
      if (group) setPickedGroupId(group.id);
    },
    [draft],
  );

  const showWidgetSettings = useCallback((widgetId: string, section?: DashboardWidgetSection) => {
    setPickedWidgetId(widgetId);
    // `null` and not the last value: reaching the panel through the widget's
    // menu is not a question about a field, and moving the user off the
    // section they were working in would be answering one they did not ask.
    setPanelSection(section ?? null);
    setPanelPulse((pulse) => pulse + 1);
  }, []);

  const clearPanelPulse = useCallback(() => setPanelPulse(0), []);

  // Same shape as the rest: the draft off the closure, one `setDraft`, and the
  // selection moved to what landed — first widget, else first group — with its
  // page, since the selection resolves against the visible page.
  const applyAssistantProposal = useCallback(
    (actions: DashboardAssistantAction[]) => {
      const result = applyProposal(draft, actions, {
        pageId: activePageId,
        groupId: activeGroupId,
      });
      if (!result.ok) return result;

      const { draft: next, landed: created } = result;
      setDraft(next);

      const widgetId = created.widgetIds[0];
      const groupId = widgetId ? widgetOps.groupOfWidget(next, widgetId)?.id : created.groupIds[0];
      const pageId = groupId ? pageOps.pageIdOfGroup(next, groupId) : undefined;
      if (pageId) setPickedPageId(pageId);
      if (groupId) setPickedGroupId(groupId);
      if (widgetId) setPickedWidgetId(widgetId);

      setLanded((current) => ({
        ids: [...created.groupIds, ...created.widgetIds],
        key: current.key + 1,
      }));
      return result;
    },
    [draft, activePageId, activeGroupId],
  );

  const clearLanded = useCallback(
    () => setLanded((current) => ({ ids: [], key: current.key })),
    [],
  );

  const value = useMemo<DashboardDraftContextValue>(
    () => ({
      draft,
      setName,
      setMainTable,
      setLinks,
      dropBlankLinks,
      resetDraft,
      dashboardId,
      hydrateDraft,
      markSaved,
      isDirty,
      activePageId,
      setActivePageId: setPickedPageId,
      addPage,
      renamePage,
      deletePage,
      activeGroupId,
      setActiveGroupId: setPickedGroupId,
      addGroup,
      renameGroup,
      moveGroup,
      reorderGroups,
      deleteGroup,
      activeWidgetId,
      setActiveWidgetId,
      addWidget,
      duplicateWidget,
      moveWidget,
      resizeWidget,
      renameWidget,
      setWidgetType,
      setWell,
      setFieldCalculation,
      addFilter,
      setFilter,
      setFilterColumn,
      removeFilter,
      setWidgetFormat,
      setWidgetDescription,
      deleteWidget,
      showWidgetSettings,
      panelPulse,
      panelSection,
      clearPanelPulse,
      applyAssistantProposal,
      landed,
      clearLanded,
      draftSession,
    }),
    [
      draft,
      setName,
      setMainTable,
      setLinks,
      dropBlankLinks,
      resetDraft,
      dashboardId,
      hydrateDraft,
      markSaved,
      isDirty,
      activePageId,
      addPage,
      renamePage,
      deletePage,
      activeGroupId,
      addGroup,
      renameGroup,
      moveGroup,
      reorderGroups,
      deleteGroup,
      activeWidgetId,
      setActiveWidgetId,
      addWidget,
      duplicateWidget,
      moveWidget,
      resizeWidget,
      renameWidget,
      setWidgetType,
      setWell,
      setFieldCalculation,
      addFilter,
      setFilter,
      setFilterColumn,
      removeFilter,
      setWidgetFormat,
      setWidgetDescription,
      deleteWidget,
      showWidgetSettings,
      panelPulse,
      panelSection,
      clearPanelPulse,
      applyAssistantProposal,
      landed,
      clearLanded,
      draftSession,
    ],
  );

  return <DashboardDraftContext.Provider value={value}>{children}</DashboardDraftContext.Provider>;
};

/**
 * Reads the draft. Throws outside the provider on purpose: a screen of this
 * feature rendered without it would silently lose everything the user typed.
 */
export function useDashboardDraftContext(): DashboardDraftContextValue {
  const context = useContext(DashboardDraftContext);
  if (!context) {
    throw new Error('useDashboardDraftContext must be used inside a DashboardDraftProvider');
  }
  return context;
}
